import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, message } from 'antd';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { ReactComponent as AuthorizeIcon } from '../../../../assets/476dab3b4f69c5886bdab2c64c134f28_e20f00.svg';
import { ReactComponent as CrossIcon } from '../../../../assets/cross.svg';

/**
 * AuthorizeExcess component for handling excess quantity authorization
 * 
 * @param {Object} props
 * @param {number} props.receivedQty - The received quantity
 * @param {number} props.expectedQty - The expected quantity (default: 1)
 * @param {Object} props.record - The current record data
 * @param {Object} props.currentUser - The current user data
 * @param {Function} props.onAuthorize - Callback function when authorization is successful
 * @param {boolean} props.isAuthorized - Whether the excess is already authorized
 * @param {string} props.type - The type of tracking stage (fabrication, qc_release, etc.)
 * @param {number} props.maxAuthorizeValue - The maximum authorize value
 */
const AuthorizeExcess = ({
  receivedQty,
  expectedQty = 1,
  record,
  currentUser,
  onAuthorize,
  isAuthorized = false,
  type = 'fabrication',
  authorizationData = null,
  maxAuthorizeValue = 0
}) => {
  // State variables
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const [authorizeReason, setAuthorizeReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [authorized, setAuthorized] = useState(isAuthorized);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [localAuthorizationData, setLocalAuthorizationData] = useState(authorizationData);
  const buttonRef = useRef(null);

  // Update authorized state when isAuthorized prop changes
  useEffect(() => {
    setAuthorized(isAuthorized);
  }, [isAuthorized]);

  // Update local authorization data when authorizationData prop changes
  useEffect(() => {
    setLocalAuthorizationData(authorizationData);
  }, [authorizationData]);

  // Convert values to numbers for comparison
  const numReceivedQty = Number(receivedQty);
  const numExpectedQty = Number(expectedQty);
  const numMaxAuthorizeValue = Number(maxAuthorizeValue);

  // Only show if received quantity exceeds expected quantity
  if (!(numReceivedQty > numExpectedQty)) {
    return null;
  }

  // If maxAuthorizeValue is set and received quantity is less than or equal to it,
  // don't show the authorization button
  if (numMaxAuthorizeValue > 0 && numReceivedQty <= numMaxAuthorizeValue && !isAuthorized) {
    return null;
  }

  const handleAuthorize = () => {
    if (!authorizeReason.trim()) {
      message.error('Please provide a reason for authorization');
      return;
    }

    setLoading(true);

    try {
      // Create authorization data
      const currentDate = moment();
      const authorizeData = {
        receivedAuthorizedById: currentUser?.id,
        receivedAuthorizedAt: currentDate.format('YYYY-MM-DD'),
        receivedAuthorizedReason: authorizeReason,
        materialControlRegisterId: record?.id,
        progressStepId: record?.progressStepId,
      };

      // Create local authorization data for immediate display
      const newLocalAuthData = {
        authorizedByName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || 'Current User',
        authorizedAt: currentDate.format('YYYY-MM-DD'),
        authorizedReason: authorizeReason
      };

      // Update local state with the new authorization data
      setLocalAuthorizationData(newLocalAuthData);

      // Call the onAuthorize callback with the data
      onAuthorize(type, authorizeData);

      // Update UI state
      setAuthorized(true);
      setDropdownVisible(false);
      setAuthorizeReason('');
      // message.success('Excess quantity authorized with reason provided');
    } catch (error) {
      message.error('Failed to authorize excess quantity');
      console.error('Authorization error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="authorize-container" style={{ position: 'relative', display: 'inline-block' }}>
      <span
        ref={buttonRef}
        className={`authorize-text ${authorized ? 'authorized' : ''}`}
        style={{ cursor: 'pointer' }} // Always show pointer cursor
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();

          // For installation step, align dropdown to the left side
          if (type === 'installation') {
            setDropdownPosition({
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX - 250
            });
          } else {
            setDropdownPosition({
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX
            });
          }

          if (authorized) {
            // Show authorization details when already authorized
            setDetailsVisible(!detailsVisible);
            setDropdownVisible(false);
          } else {
            // Show authorization dropdown when not authorized
            setDropdownVisible(!dropdownVisible);
            setDetailsVisible(false);
          }
        }}
      >
        {authorized ? 'Authorized' : 'Authorize'}
      </span>

      {dropdownVisible && ReactDOM.createPortal(
        <div
          className="dropdown-content authorize-dropdown"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dropdown-header">
            <h5>
              <span className="black-icon">
                <AuthorizeIcon />
              </span>
              Please provide reason for Authorization:
            </h5>
          </div>

          <div className="dropdown-body" style={{ fontSize: '12px' }}>
            <div style={{ marginBottom: '10px' }}>
              <Input.TextArea
                placeholder="Enter reason for authorization"
                rows={3}
                value={authorizeReason}
                onChange={(e) => setAuthorizeReason(e.target.value)}
              />
            </div>
          </div>

          <div className="dropdown-footer">
            <Button
              size="small"
              type="primary"
              loading={loading}
              onClick={handleAuthorize}
            >
              Authorize
            </Button>
            <Button
              size="small"
              onClick={() => setDropdownVisible(false)}
            >
              Cancel
            </Button>
          </div>
        </div>,
        document.body
      )}
      {/* Authorization Details Dropdown */}
      {detailsVisible && (localAuthorizationData || authorizationData) && ReactDOM.createPortal(
        <div
          className="dropdown-content authorize-dropdown"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dropdown-header">
            <h5 style={{ color: '#ff6600', fontWeight: 'bold' }}>
              Authorization Details
            </h5>
            <span
              className="close-icon"
              onClick={() => setDetailsVisible(false)}
              style={{ color: '#ff6600', position: 'absolute', top: '10px', right: '10px', cursor: 'pointer' }}
            >
              <CrossIcon width="16" height="16" />
            </span>
          </div>

          <div className="dropdown-body" style={{ fontSize: '12px' }}>
            <div style={{ marginBottom: '10px' }}>
              <p><strong>Authorized By:</strong> {(localAuthorizationData || authorizationData).authorizedByName || 'Unknown User'}</p>
              <p><strong>Date & Time:</strong> {(localAuthorizationData || authorizationData).authorizedAt ?
                moment((localAuthorizationData || authorizationData).authorizedAt).format('DD MMM YYYY [at] HH:mm') : 'Not specified'}</p>
              <p><strong>Reason:</strong> {(localAuthorizationData || authorizationData).authorizedReason || 'No reason provided'}</p>
            </div>
          </div>

          <div className="dropdown-footer">
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AuthorizeExcess;
